import React, { useState } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { qrLoginService } from '../services/qrLoginService';
import { useNotification } from '../hooks/useNotification';
import { useUserStore } from '../stores/useUserStore';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { AlertCircle, Loader2 } from 'lucide-react';
import { Scanner } from '@yudiel/react-qr-scanner';
import type { IDetectedBarcode } from '@yudiel/react-qr-scanner';

interface QRLoginScannerDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const QRLoginScannerDialog: React.FC<QRLoginScannerDialogProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const { translate } = useTranslation();
  const { notify } = useNotification();
  const { fetchUser } = useUserStore();
  const [isProcessing, setIsProcessing] = useState(false);

  const handleScan = async (detectedCodes: IDetectedBarcode[]) => {
    if (isProcessing) return;

    try {
      setIsProcessing(true);
      const qrData = detectedCodes[0].rawValue;

      // Parse the QR code URL
      // Expected format: http://localhost:8000/qr-login/{sessionId}?code={verificationCode}
      const url = new URL(qrData);
      const pathParts = url.pathname.split('/');
      const sessionId = pathParts[pathParts.length - 1]; // Get last part of path
      const verificationCode = url.searchParams.get('code');

      if (!sessionId || !verificationCode) {
        notify(
          translate(
            'pos.auth.qr_login.invalid_qr',
            'Invalid QR code. Please scan a valid login QR code.'
          ),
          { type: 'error' }
        );
        setIsProcessing(false);
        return;
      }

      // Confirm the login
      const result = await qrLoginService.confirmLogin(sessionId, verificationCode);

      if (result.success) {
        notify(
          translate('pos.auth.qr_login.confirmed', 'Login confirmed successfully!'),
          { type: 'success' }
        );
        
        // Refresh user data
        await fetchUser();
        
        onSuccess?.();
        onClose();
      } else {
        notify(
          result.message ||
            translate('pos.auth.qr_login.confirm_failed', 'Failed to confirm login'),
          { type: 'error' }
        );
        setIsProcessing(false);
      }
    } catch (error) {
      console.error('Error processing QR code:', error);
      notify(
        error instanceof Error
          ? error.message
          : translate('pos.auth.qr_login.scan_error', 'Failed to process QR code'),
        { type: 'error' }
      );
      setIsProcessing(false);
    }
  };

  const handleScannerError = (error: any) => {
    const errorMessage =
      error instanceof Error
        ? error.message
        : translate('pos.auth.qr_login.scanner_error', 'Scanner error');
    notify(errorMessage, { type: 'error' });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            {translate('pos.auth.qr_login.scan_qr', 'Scan QR Code')}
          </DialogTitle>
          <DialogDescription>
            {translate(
              'pos.auth.qr_login.scan_description',
              'Scan the QR code displayed on the desktop to switch users.'
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Scanner Component */}
          <div className="relative">
            {open && (
              <div className="relative overflow-hidden rounded-lg border">
                <Scanner
                  onScan={handleScan}
                  onError={handleScannerError}
                  scanDelay={500}
                />
                {/* Processing overlay */}
                {isProcessing && (
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                    <div className="bg-white rounded-lg p-4 shadow-lg">
                      <div className="flex items-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                        <span className="text-sm font-medium">
                          {translate('pos.auth.qr_login.processing', 'Processing...')}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Instructions */}
          <div className="space-y-2">
            <div className="flex items-start gap-2 text-sm text-muted-foreground">
              <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium">
                  {translate('pos.auth.qr_login.scan_instructions', 'Instructions')}
                </p>
                <ul className="mt-1 space-y-1 text-xs list-disc list-inside">
                  <li>
                    {translate(
                      'pos.auth.qr_login.point_camera',
                      'Point your camera at the QR code'
                    )}
                  </li>
                  <li>
                    {translate(
                      'pos.auth.qr_login.ensure_lighting',
                      'Ensure good lighting'
                    )}
                  </li>
                  <li>
                    {translate('pos.auth.qr_login.hold_steady', 'Hold your device steady')}
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
